import { context, propagation } from '@opentelemetry/api';

import { environment } from '../../../environments/environment';

import type { HttpInterceptorFn } from '@angular/common/http';

/**
 * viewer → api-server 呼び出しに W3C traceparent（および tracestate）ヘッダを付与する
 * HttpInterceptor。これにより viewer が張った trace の子として api-server 側 span が連結され、
 * viewer → api-server → AI Server / DB を 1 trace で追跡できる（E2E trace）。
 *
 * ## なぜ interceptor で明示注入するのか
 * app.config.ts の provideHttpClient は withFetch() を指定していないため、Angular HttpClient は
 * XMLHttpRequest ベースで動作する。そのため @opentelemetry/instrumentation-fetch の
 * propagateTraceHeaderCorsUrls による自動 traceparent 注入は効かない。よって本 interceptor で
 * propagation.inject(context.active(), carrier) を明示的に呼び、active span の文脈から
 * traceparent を構築してヘッダに載せる。
 *
 * ## 付与条件
 * environment.apiUrl の前方一致でのみ付与する（authInterceptor と同じ判定パターン）。
 * S3 presigned URL や外部ドメインへ traceparent を漏らさないための安全弁。
 *
 * ## active span がない / OTel 未初期化（useMock）のとき
 * propagation.inject は「有効な span context がない」「グローバル propagator が未登録（noop）」
 * のいずれでも carrier に何も書き込まない。その場合 carrier は空のままなので、ヘッダを付けず
 * 元の req をそのまま次へ流す（不要な clone も行わない）。
 */
export const traceparentInterceptor: HttpInterceptorFn = (req, next) => {
  // 外部ドメインへの traceparent 漏洩を防ぐため、environment.apiUrl の前方一致でのみ付与する。
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  // propagation.inject が W3C ヘッダ（traceparent / tracestate）を書き込む carrier。
  // active context に有効な span がなければ、または OTel 未初期化なら空のままになる。
  const carrier: Record<string, string> = {};
  propagation.inject(context.active(), carrier);

  const headerNames = Object.keys(carrier);
  if (headerNames.length === 0) {
    // 注入すべきヘッダがない（span なし / noop propagator）ので clone せず素通しする。
    return next(req);
  }

  // HttpRequest は immutable のため clone で新しいヘッダ付きリクエストを作る。
  // 既存ヘッダ・body は setHeaders の追加のみで変更されない。
  const tracedReq = req.clone({ setHeaders: carrier });
  return next(tracedReq);
};
